import {Grid2, Stack, Typography} from '@mui/material';
import type {DeleteBatchRefundProcedureParams} from 'domain/usecases/private/refund/request/delete-batch-procedure';
import {AlertActions, AlertModal, MainFooter, Snackbar, StyledGrid} from 'presentation/components';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useRefundRequest} from 'presentation/pages/private/refund/hooks';
import {translations} from 'presentation/translations-new';
import React, {Fragment, useState} from 'react';
import {FooterButton} from '../../../ReceiptRegistration/components';
import {useGetRefundProcedureTherapies} from '../../hooks/useGetRefundProcedureTherapies';
import {useTherapies} from '../../hooks/useTherapies';
import {useDeleteBatchProcedureMutation} from '../../queries/useDeleteBatchProcedureMutation';
import {CustomContainer, CustomStyledGridItem, StyledFooterContainer} from '../../styles';
import type {TherapiesProps} from '../../types';
import type {TherapyDataSchema} from '../AddTherapy/schemas/therapyDataSchema';
import {AddTherapyEmptyState} from '../AddTherapyEmptyState';
import {TherapyCard} from '../TherapyCard';
import {TherapyDetailsDrawer} from '../TherapyDetailsDrawer';
import type {ErrorState} from './types';
import {convertBRLCurrencyToNumber} from 'presentation/utils/convertBRLCurrencyToNumber';
import {sumObjectArrayNumbers} from 'presentation/utils/sumObjectArrayNumbers';
import {DetailsModalDifferentValues} from './components/DetailsModalDifferentValues';
import {formatNumberToBRLCurrency} from 'presentation/utils/formatNumberToBRLCurrency';
import {TherapyTutorials} from '../AddTherapy/components/TherapyTutorials';
import {TutorialStepsId} from '../AddTherapy/components/TherapyTutorials/types';

const {
	flowTitle,
	addAtLeastOneTherapyModal,
	removeModal,
	getTherapyTypesError,
	noMoreTherapiesModal,
	differentValuesModal,
} = translations['pt-br'].privateFlow.refund.therapies;

export const TherapyList = ({handleNextStep}: TherapiesProps) => {
	const {isTablet} = useMediaQueries();
	const {requests} = useRefundRequest();

	const {therapies, removeTherapy, handleLoadTherapyForEdit, handleGoToTherapyMode} =
		useTherapies();

	const [openAtLeastOneTherapyModal, setOpenAtLeastOneTherapyModal] = useState<boolean>(false);
	const [openRemoveModal, setOpenRemoveModal] = useState<boolean>(false);
	const [selectedTherapy, setSelectedTherapy] = useState<TherapyDataSchema | null>(null);
	const [errorState, setErrorState] = useState<ErrorState | null>(null);
	const [openNoMoreTherapiesModal, setOpenNoMoreTherapiesModal] = useState<boolean>(false);
	const [openDifferentValuesModal, setOpenDifferentValuesModal] = useState<boolean>(false);
	const [therapyIndexSelected, setTherapyIndexSelected] = useState<number | null>(null);

	const hasTherapies = therapies.length > 0;

	const totalTherapiesRaw = therapies.reduce((acc, therapy) => {
		const totalValue = sumObjectArrayNumbers(
			therapy.sessions,
			'sessionValue',
			convertBRLCurrencyToNumber,
		);
		return acc + totalValue;
	}, 0);
	const totalTherapies = Math.round(totalTherapiesRaw * 100) / 100;
	const totalValueTherapiesString = formatNumberToBRLCurrency(totalTherapies);

	const totalValueDocumentString = requests.requestData[0].financialData.invoiceAmount ?? '';
	const totalInDocument = convertBRLCurrencyToNumber(totalValueDocumentString);
	const totalValueDocumentFormatted = formatNumberToBRLCurrency(totalInDocument);

	const handleValidateNextStep = () => {
		const isValuesValid = totalTherapies === totalInDocument;
		if (!isValuesValid) {
			setOpenDifferentValuesModal(true);
			return;
		}
		handleNextStep();
	};

	const handleCloseDrawer = () => {
		setSelectedTherapy(null);
		setTherapyIndexSelected(null);
	};

	const {isTherapyTypesDataEmpty, isErrorTherapyTypes} = useGetRefundProcedureTherapies();

	const handleAddTherapy = () => {
		if (isErrorTherapyTypes) {
			setErrorState({
				type: 'getProcedureTherapies',
				message: getTherapyTypesError,
			});
			return;
		}
		if (isTherapyTypesDataEmpty) {
			setOpenNoMoreTherapiesModal(true);
			return;
		}

		handleGoToTherapyMode('add');
	};

	const handleEditTherapy = () => {
		if (therapyIndexSelected !== null) {
			handleLoadTherapyForEdit(therapyIndexSelected);
		}
		handleCloseDrawer();
	};

	const deleteBatchProcedureMutation = useDeleteBatchProcedureMutation();
	const handleRemoveTherapy = (index: number) => {
		const params: DeleteBatchRefundProcedureParams = {
			protocolCode: requests?.protocolCode?.toString() || '',
			requestCode: requests?.requestData[0]?.requestCode?.toString() || '',
			procedureCode: therapies[index]?.therapyType?.code.toString() || '',
		};
		deleteBatchProcedureMutation.mutate(params, {
			onSuccess: () => {
				removeTherapy(index);
				setOpenRemoveModal(false);
				handleCloseDrawer();
			},
			onError: () => {
				setErrorState({
					type: 'deleteBatchProcedure',
					message: removeModal.errorMessage,
				});
				setOpenRemoveModal(false);
				handleCloseDrawer();
			},
		});
	};

	return (
		<Fragment>
			<CustomContainer>
				<StyledGrid.Root marginTop={2} px={isTablet ? 2 : 3} spacing={2}>
					<CustomStyledGridItem md={12} lg={12}>
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems={'center'}
							mb={2}>
							<Typography
								variant="alertTitle"
								color="text.secondary"
								component="div"
								mb={0}>
								{flowTitle}
							</Typography>
						</Stack>
						{hasTherapies ? (
							<Stack gap={2}>
								<Grid2 container spacing={2}>
									{therapies?.map((item, index) => (
										<Grid2
											size={{
												xs: 12,
												sm: 6,
												md: 6,
												lg: 4,
											}}
											key={`${item.therapyType.code}-${index}`}>
											<TherapyCard
												data={item}
												onDetailsClick={() => {
													setSelectedTherapy(item);
													setTherapyIndexSelected(index);
												}}
											/>
										</Grid2>
									))}
								</Grid2>
								<FooterButton
									isDisabled={false}
									isLoading={false}
									primaryAction={handleValidateNextStep}
									secondaryAction={handleAddTherapy}
									isAdditionalRequestFlow
									secondaryTitle="Adicionar terapia"
									secondaryButtonId={TutorialStepsId.ADD_SESSIONS}
								/>
								<TherapyTutorials step="addSessions" />
							</Stack>
						) : (
							<Stack gap={2}>
								<AddTherapyEmptyState onAddTherapy={handleAddTherapy} />
								<FooterButton
									isDisabled={false}
									isLoading={false}
									primaryAction={() => {
										setOpenAtLeastOneTherapyModal(true);
									}}
									secondaryAction={() => null}
								/>
							</Stack>
						)}
					</CustomStyledGridItem>
				</StyledGrid.Root>
			</CustomContainer>
			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>
			<TherapyDetailsDrawer
				isOpen={!!selectedTherapy}
				onClose={handleCloseDrawer}
				therapyData={selectedTherapy}
				onRemoveTherapy={() => setOpenRemoveModal(true)}
				onEditTherapy={handleEditTherapy}
			/>
			<AlertModal
				footerContent={
					<AlertActions
						primaryAction={() => handleRemoveTherapy(therapyIndexSelected!)}
						secondaryAction={() => setOpenRemoveModal(false)}
						secondaryText={removeModal.secondaryActionText}
						primaryText={removeModal.primaryActionText}
						isLoading={deleteBatchProcedureMutation.isPending}
						isDisabled={deleteBatchProcedureMutation.isPending}
						isDisabledSecondary={deleteBatchProcedureMutation.isPending}
						flexDirection="column-reverse"
					/>
				}
				headerTitle={removeModal.title}
				title={removeModal.description}
				open={openRemoveModal}
				onClose={() => setOpenRemoveModal(false)}
				variant="info"
				customIconName="info-filled"
			/>
			<AlertModal
				footerContent={
					<AlertActions
						primaryAction={() => setOpenAtLeastOneTherapyModal(false)}
						primaryText={addAtLeastOneTherapyModal.primaryActionText}
						flexDirection="column"
					/>
				}
				headerTitle={addAtLeastOneTherapyModal.headerTitle}
				title={addAtLeastOneTherapyModal.title}
				open={openAtLeastOneTherapyModal}
				onClose={() => setOpenAtLeastOneTherapyModal(false)}
				variant="info"
				customIconName="info-filled"
			/>
			<AlertModal
				footerContent={
					<AlertActions
						primaryAction={() => setOpenDifferentValuesModal(false)}
						primaryText={differentValuesModal.primaryActionText}
						primaryBtnVariant="outlined"
					/>
				}
				headerTitle={differentValuesModal.headerTitle}
				title={differentValuesModal.title}
				description={differentValuesModal.description}
				subDescription={
					<DetailsModalDifferentValues
						totalValueTherapies={totalValueTherapiesString}
						totalValueDocument={totalValueDocumentFormatted}
					/>
				}
				open={openDifferentValuesModal}
				onClose={() => setOpenDifferentValuesModal(false)}
				variant="info"
				customIconName="info-filled"
			/>
			<AlertModal
				footerContent={
					<AlertActions
						primaryAction={() => setOpenNoMoreTherapiesModal(false)}
						primaryText={noMoreTherapiesModal.primaryActionText}
						flexDirection="column"
					/>
				}
				headerTitle={noMoreTherapiesModal.headerTitle}
				title={noMoreTherapiesModal.title}
				description={noMoreTherapiesModal.description}
				open={openNoMoreTherapiesModal}
				onClose={() => setOpenNoMoreTherapiesModal(false)}
				variant="info"
				customIconName="info-filled"
			/>
			<Snackbar
				onClose={() => setErrorState(null)}
				message={errorState?.message}
				open={!!errorState}
				anchorOrigin={{vertical: 'top', horizontal: 'right'}}
				autoHideDuration={5000}
			/>
		</Fragment>
	);
};
